import {
  escapeFileViewerHtml as escapePackageHtml,
  getFileViewerIcon as getPackageFileIcon,
  renderFileViewerContentPanel as renderPackageFileContentPanel,
  renderFileViewerShell as renderPackageFileViewer,
  renderFileViewerSidebar as renderPackageFileSidebar,
  renderFileViewerSource as renderPackageFileSource,
  splitFileViewerPath as splitPackageFilePath,
} from './file-viewer-shell';
import type { FileViewerFile, FileViewerOptions } from './file-viewer-shell';

export type PackageFileViewerFile = FileViewerFile;
export type PackageFileViewerOptions<TFile extends PackageFileViewerFile = PackageFileViewerFile> = FileViewerOptions<TFile>;

export {
  escapePackageHtml,
  getPackageFileIcon,
  renderPackageFileContentPanel,
  renderPackageFileSidebar,
  renderPackageFileSource,
  renderPackageFileViewer,
  splitPackageFilePath,
};

export type PackageFileSnapshotViewerOptions<TFile extends PackageFileViewerFile = PackageFileViewerFile> = {
  files: TFile[];
  currentIndex: number | null;
  selectFile: string;
  copyFileContent?: string;
  title: string;
  description?: string;
  emptyTitle?: string;
  emptyDescription?: string;
};

export type PublishedSkillFileViewerOptions = {
  selectFile?: string;
  copyFileContent?: string;
};

function fileCountLabel(count: number): string {
  return count + ' file' + (count === 1 ? '' : 's');
}

export function renderPackageFileSnapshotViewer<TFile extends PackageFileViewerFile>(
  options: PackageFileSnapshotViewerOptions<TFile>,
): string {
  return renderPackageFileViewer({
    files: options.files,
    currentIndex: options.currentIndex,
    selectFile: options.selectFile,
    copyFileContent: options.copyFileContent,
    sidebarHeaderHtml: '<div class="border-b border-zinc-800 bg-zinc-900/70 px-4 py-3">' +
      '<div class="text-sm font-semibold text-zinc-200">' + escapePackageHtml(options.title) + '</div>' +
      (options.description ? '<div class="mt-1 text-xs text-zinc-500">' + escapePackageHtml(options.description) + '</div>' : '') +
    '</div>',
    emptyTitle: options.emptyTitle || 'No package files',
    emptyDescription: options.emptyDescription,
  });
}

export function renderPublishedSkillFileViewer<TFile extends PackageFileViewerFile>(
  files: TFile[],
  currentIndex: number | null,
  options: PublishedSkillFileViewerOptions = {},
): string {
  return renderPackageFileSnapshotViewer({
    files,
    currentIndex,
    selectFile: options.selectFile || 'selectFile',
    copyFileContent: options.copyFileContent || 'copyFileContent',
    title: 'Package files',
    description: fileCountLabel(files.length) + ' in this published skill.',
    emptyTitle: 'No package files',
    emptyDescription: 'Republish from an ingested repository commit to store a file snapshot.',
  });
}

export function renderPublishedSkillFileUnavailableNotice(): string {
  return renderPackageFileSnapshotUnavailableNotice('No stored package snapshot files found for this skill. Please republish from an ingested repository commit.');
}

export function renderPackageFileSnapshotUnavailableNotice(message = 'No package snapshot files are available.'): string {
  return '<div class="rounded-lg border border-dashed border-amber-500/30 bg-amber-500/5 px-4 py-5 text-sm text-amber-100/90">' +
    '<div class="flex items-start gap-3">' +
      '<span class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">' +
        '<i class="fas fa-box-open" aria-hidden="true"></i>' +
      '</span>' +
      '<div class="min-w-0">' +
        '<div class="font-medium text-amber-100">Package snapshot unavailable</div>' +
        '<p class="mt-1 text-xs leading-5 text-amber-100/70">' + escapePackageHtml(message) + '</p>' +
      '</div>' +
    '</div>' +
  '</div>';
}
